import { type CSSProperties, type ReactNode, useEffect, useMemo, useRef } from 'react'
import type { Agent, Channel, DirectMessage, Message, OrganisationAgent } from '../api/types'
import { useCanHover, useViewport } from '../app/useViewport'
import { Face } from '../components/primitives'
import { radius } from '../components/primitives/shape'
import { Composer } from '../composer/Composer'
import { openAt } from '../read/openAt'
import { useSeen } from '../read/useSeen'
import { displayName, roomName, tagsReader } from '../room/format'
import { newTabFor, openLink } from '../room/openLink'
import { NEW_LINE_ATTRIBUTE, READING_WIDTH, Stream, streamPadding } from '../room/Stream'
import { type ConversationOf, PROPOSAL_START, useConversation } from './useConversation'

/** How close to the top of the conversation asks for the page before it, in pixels. */
const EARLIER_WITHIN = 80

const BUTTON: CSSProperties = {
  flex: 'none', padding: '5px 12px', borderRadius: radius.action, cursor: 'pointer',
  border: '1px solid var(--border-strong)', font: '500 12.5px var(--font-interface)', color: 'var(--text-soft)',
}

/** Whether a run proposal can no longer be started, at `now`. */
export const expired = (message: DirectMessage, now: number) =>
  message.proposed_run !== null && Date.parse(message.proposed_run.expires_at) <= now

/** A private message drawn by the room's own row, which takes a room message. An
 * expired proposal nobody answered keeps no answers, since Start would start nothing. */
export function asMessage(message: DirectMessage, agentNickname: string | null, now = Date.now()): Message {
  const { id, posted_at, sender, sender_type, body, headline, reply_to, answered_with } = message
  const options = expired(message, now) && answered_with === null ? null : message.options
  return {
    id, posted_at, sender, sender_type, body, headline, reply_to, options, answered_with,
    mentions: [], event: null, replies: [], reactions: [], reaches: [], shown_in_room: false,
    sender_nickname: sender_type === 'agent' ? agentNickname : null,
  }
}

/** The agent as the room's row knows one, for its face, its label and its name as a mention. */
export function asRosterAgent(agent: OrganisationAgent): Agent {
  const { name, nickname, avatar_key, summary, title, online, surface = null, open_url = null, remote_url = null } = agent
  return {
    name, nickname, avatar_key, summary, title, online, surface, open_url, remote_url, owner: null, role: null,
    biography: null, summary_updated_at: null, branch: null, worktree: null, claims: [],
    started_by_run: null,
  }
}

export interface DirectViewProps extends ConversationOf {
  channels: Channel[]
  /** A phone's way back to the tab it came from. */
  back: ReactNode
  /** Open the room on the thread `message` is in, where a reply to it is written. */
  onOpenThread: (channelId: string, message: Message) => void
}

/** Everything between you and one of your agents, privately and in its rooms. */
export function DirectView({ channels, back, onOpenThread, ...of }: DirectViewProps) {
  const { name, agent, myName } = of
  const { compact, narrow } = useViewport()
  const canHover = useCanHover()
  const link = openLink(agent, canHover)
  const conversation = useConversation(of)
  const { loadEarlier, earlier, unread, newLineBefore, markSeen } = conversation
  const composer = useRef<HTMLDivElement>(null)
  const log = useRef<HTMLDivElement>(null)
  const roster = useMemo(() => (agent ? [asRosterAgent(agent)] : []), [agent])
  const items = conversation.items ?? []
  useSeen(log, unread, markSeen)

  // Once the page has landed, open at the New line, once for this agent: a
  // message arriving later must not pull a reader back from their history.
  // Earlier pages then load above without moving what is in view, since the
  // log counts its scroll from the bottom.
  const loaded = conversation.items !== null
  const openedFor = useRef<string | null>(null)
  useEffect(() => {
    const node = log.current
    if (!node || !loaded || openedFor.current === name) return
    openedFor.current = name
    if (newLineBefore) openAt(node, node.querySelector(`[${NEW_LINE_ATTRIBUTE}]`), true)
  }, [loaded, newLineBefore, name])

  // A first page shorter than the log leaves no scrollbar to reach the top with.
  useEffect(() => {
    const node = log.current
    if (node && earlier && node.scrollHeight <= node.clientHeight) loadEarlier()
  }, [items.length, earlier, loadEarlier])
  const byId = new Map(items.map((item) => [item.message.id, item]))

  const nameOfRoom = (repositoryKey: string) => roomName(
    channels.find((channel) => channel.repository_key === repositoryKey)
      ?? { repository_key: repositoryKey, github_full_name: null })
  const room = agent?.repository_key ? nameOfRoom(agent.repository_key) : null
  const waiting = (agent?.waiting ?? 0) > 0
  const surface = agent?.surface
  const nickname = agent?.nickname ?? null

  const actionsOf = (message: Message) => {
    const item = byId.get(message.id)
    const run = item?.place === 'direct' ? item.message.proposed_run : null
    if (item?.place === 'direct' && run) {
      const { id } = item.message
      const answered = item.message.answered_with !== null || items.some((other) =>
        other.place === 'direct' && other.message.sender_type === 'human' && other.message.reply_to === id)
      if (answered) return null
      return (
        <p style={{ margin: '6px 0 0', font: '400 12px var(--font-interface)', color: 'var(--text-meta)' }}>
          {expired(item.message, Date.now())
            ? `Expired: a proposal starts within a day. Ask ${displayName(name, nickname)} again.`
            : `Start runs the task above in #${nameOfRoom(run.repository_key)} on your default machine.`}
        </p>
      )
    }
    if (item?.place !== 'room' || !tagsReader(message, myName)) return null
    return (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
        <button type="button" onClick={() => composer.current?.querySelector('textarea')?.focus()} style={BUTTON}>
          Reply here
        </button>
        <button type="button" onClick={() => onOpenThread(item.channel_id, item.message)} style={BUTTON}>
          Reply in #{nameOfRoom(item.room)}
        </button>
      </div>
    )
  }

  return (
    <>
      <header
        style={{
          padding: compact ? 'calc(10px + var(--safe-top)) 16px 11px' : '14px 20px',
          borderBottom: '1px solid var(--border)', flex: 'none', minWidth: 0,
          display: 'flex', alignItems: 'center', gap: 10,
        }}
      >
        {back}
        <Face name={name} size={24} avatarKey={agent?.avatar_key} />
        <div style={{
          display: 'flex', flexDirection: compact ? 'column' : 'row', alignItems: compact ? 'stretch' : 'baseline',
          gap: compact ? 1 : 10, flex: '0 1 auto', minWidth: 0,
        }}>
          <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
            <h1 style={{ margin: 0, fontFamily: nickname ? 'var(--font-interface)' : 'var(--font-machine)', fontSize: 14, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {displayName(name, nickname)}
            </h1>
            {nickname && (
              <span style={{ fontFamily: 'var(--font-machine)', fontSize: 11.5, color: 'var(--text-meta)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {name}
              </span>
            )}
          </div>
          <span style={{ font: '400 12px var(--font-interface)', color: 'var(--text-meta)', whiteSpace: 'nowrap' }}>
            {surface}
            {surface && waiting && ' · '}
            {waiting && <span style={{ color: 'var(--accent)' }}>waiting on you</span>}
          </span>
        </div>
        <span style={{ flex: 1 }} />
        {link && (
          <a href={link} {...newTabFor(link)} aria-label="Open in Claude app" className="sp-tap" style={BUTTON}>
            {compact ? 'Open' : 'Open in Claude app'}
          </a>
        )}
      </header>

      <div
        ref={log}
        role="log"
        aria-label={`Conversation with ${name}`}
        onScroll={(event) => {
          const node = event.currentTarget
          // Reversed, so the newest is where it starts and scrolling up is negative.
          if (node.scrollHeight - node.clientHeight + node.scrollTop < EARLIER_WITHIN) conversation.loadEarlier()
        }}
        style={{
          flex: 1, minHeight: 0, overflowY: 'auto', display: 'flex', flexDirection: 'column-reverse',
          padding: streamPadding(compact), overscrollBehaviorY: 'contain',
        }}
      >
        <div>
          {conversation.failed && (
            <p role="alert" style={{ textAlign: 'center', fontSize: 13, color: 'var(--text-dim)' }}>
              Could not load this conversation right now.
            </p>
          )}
          {conversation.items && !conversation.earlier && (
            <div style={{ display: 'flex', gap: 14, padding: '40px 0 8px' }}>
              <Face name={name} size={44} avatarKey={agent?.avatar_key} />
              <div style={{ minWidth: 0 }}>
                <div style={{ fontFamily: nickname ? 'var(--font-interface)' : 'var(--font-machine)', fontSize: 15, fontWeight: 600, overflowWrap: 'anywhere' }}>
                  {displayName(name, nickname)}
                </div>
                {nickname && (
                  <div style={{ fontFamily: 'var(--font-machine)', fontSize: 12, color: 'var(--text-faint)', overflowWrap: 'anywhere' }}>{name}</div>
                )}
                <p style={{ margin: '6px 0 0', maxWidth: 500, fontSize: 13, lineHeight: 1.6, color: 'var(--text-dim)' }}>
                  Everything between you and this agent, here{room && ` and in #${room}`}. What you send
                  here stays private unless you also send it to the room.
                </p>
              </div>
            </div>
          )}
          {items.length > 0 && (
            <Stream
              messages={items.map((item) => (item.place === 'room' ? item.message : asMessage(item.message, nickname)))}
              unread={unread}
              newLineBefore={newLineBefore}
              roster={roster}
              channelId={agent?.channel_id ?? ''}
              selectedId={null}
              myName={myName}
              onOpenThread={(message) => {
                const item = byId.get(message.id)
                if (item?.place === 'room') onOpenThread(item.channel_id, item.message)
              }}
              onAnswer={(message, answer) => {
                const item = byId.get(message.id)
                if (item?.place === 'room') return conversation.answerInRoom(item, answer)
                if (item?.message.proposed_run && answer === PROPOSAL_START) return conversation.startProposal(item)
                return conversation.send(answer, false, message.id)
              }}
              conversation={{
                placeOf: (message) => {
                  const item = byId.get(message.id)
                  return item?.place === 'room' ? nameOfRoom(item.room) : null
                },
                actionsOf,
              }}
            />
          )}
        </div>
      </div>

      <div ref={composer} style={{ width: '100%', maxWidth: READING_WIDTH, margin: '0 auto' }}>
        <Composer
          roster={roster}
          nobodyRunning={false}
          narrow={narrow}
          onSend={conversation.send}
          direct={{ agent: name, alsoTo: agent?.online ? room : null }}
        />
      </div>
    </>
  )
}
